import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
} from "recharts";
import { getStrategies, fmtPrice } from "@/lib/api";
import { bsPrice } from "@/lib/bs";
import { Layers3, TrendingUp, TrendingDown, Minus, Zap, Clock } from "lucide-react";

const R = 0.045;
const OUTLOOK = {
  bullish: { c: "text-bull", bg: "bg-bull/10 border-bull/25", Icon: TrendingUp },
  bearish: { c: "text-bear", bg: "bg-bear/10 border-bear/25", Icon: TrendingDown },
  neutral: { c: "text-neutral", bg: "bg-neutral/10 border-neutral/25", Icon: Minus },
  volatile: { c: "text-warn", bg: "bg-warn/10 border-warn/25", Icon: Zap },
};

const payoffData = (legs, netCost, days) => {
  const strikes = legs.map((l) => l.strike);
  const lo = Math.min(...strikes) * 0.9;
  const hi = Math.max(...strikes) * 1.1;
  const T = Math.max(days, 0) / 365;
  const N = 60;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const p = lo + ((hi - lo) * i) / N;
    let plExp = 0;
    let plNow = 0;
    legs.forEach((l) => {
      const isCall = l.type === "call";
      const sign = l.action === "buy" ? 1 : -1;
      const intr = isCall ? Math.max(p - l.strike, 0) : Math.max(l.strike - p, 0);
      plExp += sign * intr;
      plNow += sign * bsPrice(p, l.strike, T, Math.max(l.iv || 0.4, 0.05), R, isCall);
    });
    pts.push({ price: +p.toFixed(2), plExp: +(plExp - netCost).toFixed(2), plNow: +(plNow - netCost).toFixed(2) });
  }
  return pts;
};

const PLTip = ({ active, payload, days }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-app border border-border rounded p-2 text-[11px] font-mono space-y-0.5">
      <div className="text-tertiary">@ ${d.price}</div>
      <div className={d.plNow >= 0 ? "text-ai" : "text-bear"}>Now ({days}d) {d.plNow >= 0 ? "+" : ""}${d.plNow.toFixed(2)}</div>
      <div className={d.plExp >= 0 ? "text-bull" : "text-bear"}>Expiry {d.plExp >= 0 ? "+" : ""}${d.plExp.toFixed(2)}</div>
    </div>
  );
};

const Metric = ({ label, value, color = "text-white" }) => (
  <div className="flex flex-col">
    <span className="text-[9px] uppercase tracking-widest text-tertiary font-mono">{label}</span>
    <span className={`text-xs font-mono ${color}`}>{value}</span>
  </div>
);

const StrategyCard = ({ s, spot, days }) => {
  const o = OUTLOOK[s.outlook] || OUTLOOK.neutral;
  const data = payoffData(s.legs, s.net_cost, days);
  const isCredit = s.kind === "credit";
  const spotX = data.reduce((a, b) => (Math.abs(b.price - spot) < Math.abs(a.price - spot) ? b : a)).price;
  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden" data-testid={`strategy-${s.id}`}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-white text-sm">{s.name}</span>
          <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border flex items-center gap-1 ${o.bg} ${o.c}`}>
            <o.Icon size={9} /> {s.outlook}
          </span>
        </div>
        <span className={`text-xs font-mono font-bold ${isCredit ? "text-bull" : "text-secondary"}`}>
          {isCredit ? `+$${Math.abs(s.net_cost).toFixed(2)} cr` : `-$${s.net_cost.toFixed(2)} db`}
        </span>
      </div>

      <div className="px-1 pt-2">
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#1A1A1D" vertical={false} />
            <XAxis dataKey="price" tick={{ fill: "#71717A", fontSize: 8, fontFamily: "IBM Plex Mono" }} interval={14} axisLine={{ stroke: "#222225" }} tickLine={false} tickFormatter={(v) => `$${Math.round(v)}`} />
            <YAxis tick={{ fill: "#71717A", fontSize: 8, fontFamily: "IBM Plex Mono" }} width={34} axisLine={false} tickLine={false} />
            <Tooltip content={<PLTip days={days} />} />
            <ReferenceLine y={0} stroke="#3a3a3e" strokeWidth={1} />
            <ReferenceLine x={spotX} stroke="#007AFF" strokeDasharray="3 3" />
            {s.breakevens.map((b, i) => (
              <ReferenceLine key={i} x={data.reduce((a, c) => (Math.abs(c.price - b) < Math.abs(a.price - b) ? c : a)).price} stroke="#FF9F0A" strokeDasharray="2 2" />
            ))}
            <Line type="monotone" dataKey="plExp" stroke="#52525b" strokeWidth={1.25} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="plNow" stroke="#00F0FF" strokeWidth={1.75} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-end gap-3 px-3 pb-1 text-[9px] font-mono text-tertiary uppercase">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-ai inline-block" /> Now</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0 border-t border-dashed border-[#52525b] inline-block" /> At expiry</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 px-3 py-2 border-t border-border-subtle">
        <Metric label="Max P" value={s.max_profit === "Unlimited" ? "∞" : `$${s.max_profit}`} color="text-bull" />
        <Metric label="Max L" value={`$${s.max_loss}`} color="text-bear" />
        <Metric label="Break-even" value={s.breakevens.map((b) => `$${fmtPrice(b)}`).join(" / ")} color="text-warn" />
        <Metric label="Net Δ" value={s.net_delta} />
      </div>
      <div className="grid grid-cols-4 gap-2 px-3 pb-2">
        <Metric label="Net Θ" value={s.net_theta} color="text-warn" />
        <Metric label="Net Γ" value={s.net_gamma} color="text-ai" />
        <Metric label="Net V" value={s.net_vega} />
        <Metric label="Legs" value={s.legs.length} />
      </div>
      <div className="px-3 pb-3 pt-1 border-t border-border-subtle">
        <div className="flex flex-wrap gap-1 mb-1.5">
          {s.legs.map((l, i) => (
            <span key={i} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${l.action === "buy" ? "bg-bull/5 text-bull border-bull/20" : "bg-bear/5 text-bear border-bear/20"}`}>
              {l.action === "buy" ? "+" : "−"} {l.type.toUpperCase()} ${l.strike} @ {l.price}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-tertiary leading-snug">{s.description}</p>
      </div>
    </div>
  );
};

export const StrategyPanel = ({ symbol }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["strategies", symbol],
    queryFn: () => getStrategies(symbol),
    refetchInterval: 30000,
    enabled: !!symbol,
  });
  const [days, setDays] = useState(null);
  const dte = data?.dte || 14;
  useEffect(() => { if (data && days === null) setDays(data.dte); }, [data, days, setDays]);
  const effDays = days === null ? dte : Math.min(days, dte);

  if (!symbol) return null;
  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden" data-testid="strategy-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-app/60">
        <div className="flex items-center gap-2">
          <Layers3 size={15} className="text-ai" />
          <h4 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Multi-Leg Strategies</h4>
        </div>
        {data && (data.priced === "polygon" ? (
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-bull/10 text-bull border border-bull/25">Live · Polygon</span>
        ) : data.priced === "partial" ? (
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-warn/10 text-warn border border-warn/25">Polygon + Model</span>
        ) : (
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-surface-2 text-tertiary border border-border">Model priced</span>
        ))}
      </div>

      {data && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle" data-testid="payoff-slider">
          <Clock size={13} className="text-ai shrink-0" />
          <span className="text-[10px] font-mono uppercase text-tertiary shrink-0">Days to expiry</span>
          <input type="range" min={0} max={dte} value={effDays}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="flex-1 accent-[#00F0FF] cursor-pointer" data-testid="payoff-range" />
          <span className="font-mono text-sm text-ai w-14 text-right shrink-0">
            {effDays === 0 ? "Expiry" : `${effDays}d`}
          </span>
        </div>
      )}

      {isLoading || !data ? (
        <div className="p-8 text-center text-tertiary font-mono text-xs">Building strategies…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 p-3">
          {data.strategies.map((s) => <StrategyCard key={s.id} s={s} spot={data.spot} days={effDays} />)}
        </div>
      )}
    </div>
  );
};
